import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search as SearchIcon } from "lucide-react";
import { api } from "../lib/apiClient";
import { Navbar } from "../components/Navbar";
import { VideoCard } from "../components/VideoCard";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [term, setTerm] = useState(q);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const runSearch = useCallback((query) => {
    const clean = query.trim();
    if (!clean) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    api
      .get(`/search?q=${encodeURIComponent(clean)}`)
      .then((r) => setResults(r.data.results || []))
      .catch(() => setResults([]))
      .finally(() => {
        setLoading(false);
        setSearched(true);
      });
  }, []);

  useEffect(() => {
    setTerm(q);
    runSearch(q);
  }, [q, runSearch]);

  // Live search: update the URL/results a moment after the viewer stops typing.
  useEffect(() => {
    const t = setTimeout(() => {
      const clean = term.trim();
      if (clean !== q) setParams(clean ? { q: clean } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const onSubmit = (e) => {
    e.preventDefault();
    setParams(term.trim() ? { q: term.trim() } : {});
  };

  return (
    <div className="min-h-screen bg-[#030305]">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-24">
        <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Find something to watch</p>
        <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-tighter mb-8">Search</h1>

        <form onSubmit={onSubmit} className="relative max-w-2xl mb-12">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            data-testid="search-page-input"
            autoFocus
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search shows, creators, channels…"
            className="w-full bg-white/5 border border-white/15 focus:border-[#FF2A2A] rounded-sm pl-12 pr-4 py-4 text-white placeholder:text-zinc-500 outline-none transition-colors"
          />
        </form>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#FF2A2A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : searched && results.length === 0 ? (
          <div className="border border-white/10 rounded-sm p-16 text-center" data-testid="search-empty">
            <p className="text-zinc-400">No results for “{q}”. Try another title, creator or channel.</p>
          </div>
        ) : results.length > 0 ? (
          <>
            <p className="text-sm text-zinc-500 mb-6" data-testid="search-result-count">
              {results.length} result{results.length !== 1 ? "s" : ""} for “{q}”
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="search-results">
              {results.map((v, i) => (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: (i % 8) * 0.03 }}
                >
                  <VideoCard video={v} />
                </motion.div>
              ))}
            </div>
          </>
        ) : (
          <p className="text-zinc-500" data-testid="search-prompt">Start typing to search across all 7 channels.</p>
        )}
      </div>
    </div>
  );
}
